import * as oauth from 'oauth4webapi';

// Storage keys
const STORAGE_KEY_STATE = 'oidc.state';
const STORAGE_KEY_CODE_VERIFIER = 'oidc.code_verifier';
const STORAGE_KEY_NONCE = 'oidc.nonce';
const STORAGE_KEY_USER = 'oidc.user';
const STORAGE_KEY_CALLBACK_PROCESSING = 'oidc.callback_processing';

// User Manager class to maintain compatibility with oidc-client API
class UserManager {
  constructor(settings) {
    this.settings = {
      authority: settings.authority,
      redirect_uri: settings.redirect_uri,
      post_logout_redirect_uri: settings.post_logout_redirect_uri,
      client_id: settings.client_id,
      response_type: settings.response_type || 'code',
      scope: settings.scope,
      userStore: settings.userStore || localStorage,
      clockTolerance: settings.clock_tolerance ?? 300,
      enableDebug: settings.enableDebug ?? false
    };

    this.client = {
      client_id: settings.client_id,
      token_endpoint_auth_method: 'none', // PKCE flow
      [oauth.clockTolerance]: settings.clock_tolerance ?? 300
    };

    this._issuer = null;
    this._log('UserManager initialized', { authority: this.settings.authority, client_id: this.settings.client_id });
  }

  // Internal logging method - only logs when debug is enabled
  _log(message, data = null) {
    if (this.settings.enableDebug) {
      const timestamp = new Date().toISOString();
      console.log(`[UserManager ${timestamp}] ${message}`, data || '');
    }
  }

  async _getIssuer() {
    if (this._issuer) {
      this._log('Using cached issuer');
      return this._issuer;
    }

    this._log('Discovering issuer configuration', { authority: this.settings.authority });
    const issuerUrl = new URL(this.settings.authority);
    const response = await oauth.discoveryRequest(issuerUrl);
    this._issuer = await oauth.processDiscoveryResponse(issuerUrl, response);
    this._log('Issuer configuration loaded', {
      authorization_endpoint: this._issuer.authorization_endpoint,
      token_endpoint: this._issuer.token_endpoint,
      userinfo_endpoint: this._issuer.userinfo_endpoint,
      end_session_endpoint: this._issuer.end_session_endpoint,
      supports_PAR: !!this._issuer.pushed_authorization_request_endpoint
    });
    return this._issuer;
  }

  async signinRedirect(args = {}) {
    try {
      this._log('Starting signin redirect', { extraQueryParams: args.extraQueryParams });
      const issuer = await this._getIssuer();

      // Generate PKCE parameters
      this._log('Generating PKCE parameters');
      const code_verifier = oauth.generateRandomCodeVerifier();
      const code_challenge = await oauth.calculatePKCECodeChallenge(code_verifier);
      const code_challenge_method = 'S256';

      // Generate state and nonce
      const state = oauth.generateRandomState();
      const nonce = oauth.generateRandomNonce();
      this._log('Generated state, nonce and PKCE', { state, nonce, code_challenge_method });

      // Store for callback
      localStorage.setItem(STORAGE_KEY_STATE, state);
      localStorage.setItem(STORAGE_KEY_CODE_VERIFIER, code_verifier);
      localStorage.setItem(STORAGE_KEY_NONCE, nonce);
      this._log('Stored state, code_verifier and nonce in localStorage');

      // Build authorization URL
      this._log('Building authorization URL');
      const authorizationUrl = new URL(issuer.authorization_endpoint);
      authorizationUrl.searchParams.set('client_id', this.client.client_id);
      authorizationUrl.searchParams.set('redirect_uri', this.settings.redirect_uri);
      authorizationUrl.searchParams.set('response_type', this.settings.response_type);
      authorizationUrl.searchParams.set('scope', this.settings.scope);
      authorizationUrl.searchParams.set('state', state);
      authorizationUrl.searchParams.set('nonce', nonce);
      authorizationUrl.searchParams.set('code_challenge', code_challenge);
      authorizationUrl.searchParams.set('code_challenge_method', code_challenge_method);

      // Add extra query params if provided
      if (args.extraQueryParams) {
        this._log('Adding extra query params', args.extraQueryParams);
        Object.keys(args.extraQueryParams).forEach(key => {
          authorizationUrl.searchParams.set(key, args.extraQueryParams[key]);
        });
      }

      // Check if PAR is supported
      if (issuer.pushed_authorization_request_endpoint) {
        this._log('Using Pushed Authorization Request (PAR)', { endpoint: issuer.pushed_authorization_request_endpoint });
        try {
          // Use Pushed Authorization Request (PAR)
          const params = new URLSearchParams();
          authorizationUrl.searchParams.forEach((value, key) => {
            if (key !== 'client_id') { // client_id will be added separately
              params.set(key, value);
            }
          });

          const parResponse = await oauth.pushedAuthorizationRequest(
            issuer,
            this.client,
            params
          );

          this._log('PAR response received', { status: parResponse.status, ok: parResponse.ok });

          // Check if response is valid
          if (!parResponse.ok) {
            this._log('PAR response not OK, falling back to standard authorization', { status: parResponse.status });
            window.location.href = authorizationUrl.href;
            return;
          }

          // Parse response manually if oauth4webapi is too strict
          let result;
          try {
            this._log('Processing PAR response');
            result = await oauth.processPushedAuthorizationResponse(
              issuer,
              this.client,
              parResponse
            );
            this._log('PAR response processed successfully');
          } catch (processError) {
            this._log('PAR processing error, attempting manual parse', { error: processError.message });

            // Manual fallback: parse the JSON directly
            const parResponseClone = parResponse.clone();
            const parData = await parResponseClone.json();

            if (parData.request_uri && parData.expires_in) {
              this._log('Manual PAR parse successful', { request_uri: parData.request_uri, expires_in: parData.expires_in });
              result = parData;
            } else {
              this._log('PAR response missing required fields', parData);
              throw new Error('PAR response missing required fields');
            }
          }

          // Build final authorization URL with request_uri
          const finalAuthUrl = new URL(issuer.authorization_endpoint);
          finalAuthUrl.searchParams.set('client_id', this.client.client_id);
          finalAuthUrl.searchParams.set('request_uri', result.request_uri);

          this._log('Redirecting to authorization endpoint with PAR', { url: finalAuthUrl.href });
          window.location.href = finalAuthUrl.href;
        } catch (parError) {
          // Fall back to standard authorization request if PAR fails
          this._log('PAR failed, falling back to standard authorization', { error: parError.message });
          window.location.href = authorizationUrl.href;
        }
      } else {
        // Standard authorization request (no PAR)
        this._log('PAR not supported, using standard authorization request', { url: authorizationUrl.href });
        window.location.href = authorizationUrl.href;
      }
    } catch (error) {
      this._log('Error during signin redirect', { error: error.message, stack: error.stack });
      console.error('Error during signin redirect:', error);
      throw error;
    }
  }

  async signinCallback(url = window.location.href) {
    try {
      this._log('Starting signin callback', { url });
      const issuer = await this._getIssuer();
      const currentUrl = new URL(url);

      // Check if parameters are in hash (Vue Router hash mode) or query string
      let searchParams;
      if (currentUrl.hash && currentUrl.hash.includes('?')) {
        // Parameters are in the hash: #/redirect?code=...
        this._log('OAuth params found in hash');
        const hashParts = currentUrl.hash.split('?');
        searchParams = new URLSearchParams(hashParts[1]);
      } else {
        // Parameters are in regular query string
        this._log('OAuth params in query string');
        searchParams = currentUrl.searchParams;
      }

      // Check if this URL has OAuth callback parameters
      const hasCode = searchParams.has('code');
      const hasState = searchParams.has('state');
      const hasError = searchParams.has('error');

      this._log('Checking OAuth callback parameters', { hasCode, hasState, hasError });

      if (!hasCode && !hasState && !hasError) {
        this._log('No OAuth parameters found in URL, not a callback');
        console.warn('No OAuth parameters found in URL, not a callback');
        throw new Error('NOT_A_CALLBACK');
      }

      // Check if callback is already being processed
      const isProcessing = sessionStorage.getItem(STORAGE_KEY_CALLBACK_PROCESSING);
      if (isProcessing) {
        this._log('Callback already in progress, aborting');
        throw new Error('CALLBACK_IN_PROGRESS');
      }

      // Check if user is already authenticated
      const existingUser = await this.getUser();
      if (existingUser) {
        this._log('User already authenticated, returning existing user');
        return existingUser;
      }

      // Mark callback as being processed
      sessionStorage.setItem(STORAGE_KEY_CALLBACK_PROCESSING, 'true');
      this._log('Marked callback as processing');

      // Retrieve stored values
      const expectedState = localStorage.getItem(STORAGE_KEY_STATE);
      const code_verifier = localStorage.getItem(STORAGE_KEY_CODE_VERIFIER);
      const expectedNonce = localStorage.getItem(STORAGE_KEY_NONCE);

      this._log('Retrieved stored values', { hasState: !!expectedState, hasCodeVerifier: !!code_verifier, hasNonce: !!expectedNonce });

      if (!expectedState || !code_verifier || !expectedNonce) {
        // State/verifier/nonce not found - likely already processed or not initiated properly
        this._log('State, code_verifier or nonce missing from localStorage');
        sessionStorage.removeItem(STORAGE_KEY_CALLBACK_PROCESSING);
        throw new Error('CALLBACK_STATE_MISSING');
      }

      // For hash-based URLs, we need to construct a proper URL for oauth4webapi
      // It expects parameters in the query string, not the hash
      let callbackUrl;
      if (currentUrl.hash && currentUrl.hash.includes('?')) {
        // Convert hash params to query params for validation
        this._log('Converting hash params to query params for validation');
        const hashParts = currentUrl.hash.split('?');
        callbackUrl = new URL(currentUrl.origin + currentUrl.pathname);
        callbackUrl.search = '?' + hashParts[1];
      } else {
        callbackUrl = currentUrl;
      }
      this._log('Callback URL prepared', { callbackUrl: callbackUrl.href });

      // Validate the callback parameters
      this._log('Validating auth response');
      const params = oauth.validateAuthResponse(
        issuer,
        this.client,
        callbackUrl,
        expectedState
      );

      this._log('Auth response validated', { hasError: oauth.isOAuth2Error(params) });

      if (oauth.isOAuth2Error(params)) {
        this._log('OAuth error in response', { error: params.error, description: params.error_description });
        throw new Error(`OAuth error: ${params.error} - ${params.error_description}`);
      }

      // Exchange authorization code for tokens
      this._log('Exchanging authorization code for tokens');
      const tokenResponse = await oauth.authorizationCodeGrantRequest(
        issuer,
        this.client,
        params,
        this.settings.redirect_uri,
        code_verifier
      );

      this._log('Token response received', { status: tokenResponse.status, ok: tokenResponse.ok });

      const result = await oauth.processAuthorizationCodeOpenIDResponse(
        issuer,
        this.client,
        tokenResponse,
        expectedNonce
      );

      this._log('Token response processed', { hasAccessToken: !!result.access_token, hasRefreshToken: !!result.refresh_token, hasIdToken: !!result.id_token });

      if (oauth.isOAuth2Error(result)) {
        this._log('Token error', { error: result.error });
        throw new Error(`Token error: ${result.error}`);
      }

      // Get user info
      this._log('Requesting user info');
      const userinfoResponse = await oauth.userInfoRequest(
        issuer,
        this.client,
        result.access_token
      );

      this._log('User info response received', { status: userinfoResponse.status, ok: userinfoResponse.ok });

      const claims = await oauth.processUserInfoResponse(
        issuer,
        this.client,
        oauth.skipSubjectCheck,
        userinfoResponse
      );

      this._log('User info processed', { hasSub: !!claims.sub, claimsCount: Object.keys(claims).length });

      // Create user object
      const user = {
        access_token: result.access_token,
        token_type: result.token_type || 'Bearer',
        expires_at: result.expires_in ? Math.floor(Date.now() / 1000) + result.expires_in : undefined,
        refresh_token: result.refresh_token,
        id_token: result.id_token,
        profile: claims
      };

      this._log('User object created', { expires_at: user.expires_at, hasRefreshToken: !!user.refresh_token });

      // Store user
      await this.storeUser(user);

      // Clean up storage
      localStorage.removeItem(STORAGE_KEY_STATE);
      localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);
      localStorage.removeItem(STORAGE_KEY_NONCE);
      sessionStorage.removeItem(STORAGE_KEY_CALLBACK_PROCESSING);
      this._log('Cleaned up storage, signin callback complete');

      return user;
    } catch (error) {
      // Clean up on error
      this._log('Error during signin callback', { error: error.message, stack: error.stack });
      localStorage.removeItem(STORAGE_KEY_STATE);
      localStorage.removeItem(STORAGE_KEY_CODE_VERIFIER);
      localStorage.removeItem(STORAGE_KEY_NONCE);
      sessionStorage.removeItem(STORAGE_KEY_CALLBACK_PROCESSING);
      throw error;
    }
  }

  async getUser() {
    this._log('Getting user from storage');
    const userJson = this.settings.userStore.getItem(STORAGE_KEY_USER);
    if (!userJson) {
      this._log('No user found in storage');
      return null;
    }

    try {
      const user = JSON.parse(userJson);

      // Check if token is expired
      if (user.expires_at && user.expires_at < Math.floor(Date.now() / 1000)) {
        this._log('User token expired', { expires_at: user.expires_at, now: Math.floor(Date.now() / 1000) });
        return null;
      }

      this._log('User retrieved from storage', { hasAccessToken: !!user.access_token, expires_at: user.expires_at });
      return user;
    } catch (error) {
      this._log('Error parsing user from storage', { error: error.message });
      return null;
    }
  }

  async storeUser(user) {
    this._log('Storing user in storage', { expires_at: user.expires_at });
    this.settings.userStore.setItem(STORAGE_KEY_USER, JSON.stringify(user));
  }

  async removeUser() {
    this._log('Removing user from storage');
    this.settings.userStore.removeItem(STORAGE_KEY_USER);
  }

  async signoutRedirect() {
    try {
      this._log('Starting signout redirect');
      const issuer = await this._getIssuer();
      const user = await this.getUser();

      // Remove user from storage
      await this.removeUser();

      // Build end session URL
      if (issuer.end_session_endpoint) {
        this._log('Building end session URL', { endpoint: issuer.end_session_endpoint });
        const endSessionUrl = new URL(issuer.end_session_endpoint);

        if (user && user.id_token) {
          endSessionUrl.searchParams.set('id_token_hint', user.id_token);
          this._log('Added id_token_hint to end session URL');
        }

        if (this.settings.post_logout_redirect_uri) {
          endSessionUrl.searchParams.set('post_logout_redirect_uri', this.settings.post_logout_redirect_uri);
          this._log('Added post_logout_redirect_uri', { uri: this.settings.post_logout_redirect_uri });
        }

        this._log('Redirecting to end session endpoint', { url: endSessionUrl.href });
        window.location.href = endSessionUrl.href;
      } else {
        // No end session endpoint, just redirect to post logout URL
        this._log('No end session endpoint, redirecting to post_logout_redirect_uri');
        if (this.settings.post_logout_redirect_uri) {
          window.location.href = this.settings.post_logout_redirect_uri;
        }
      }
    } catch (error) {
      this._log('Error during signout redirect', { error: error.message, stack: error.stack });
      throw error;
    }
  }
}

// Helper class for URL utilities (compatibility with oidc-client)
export class UrlUtility {
  static parseUrlFragment(url, delimiter = '#') {
    const parts = url.split(delimiter);
    if (parts.length < 2) return {};

    const fragment = parts[1];
    const params = new URLSearchParams(fragment);
    const result = {};

    for (const [key, value] of params) {
      result[key] = value;
    }

    return result;
  }
}

// Helper class for User (compatibility with oidc-client)
export class User {
  constructor(data) {
    this.access_token = data.access_token;
    this.token_type = data.token_type || 'Bearer';
    this.profile = data.profile || {};
    this.expires_at = data.expires_at;
    this.refresh_token = data.refresh_token;
    this.id_token = data.id_token;
  }
}

// Helper class for UserInfo Service (compatibility with oidc-client)
export class UserInfoService {
  constructor(settings) {
    this.settings = settings;
    this._issuer = null;
  }

  async _getIssuer() {
    if (this._issuer) return this._issuer;

    const issuerUrl = new URL(this.settings.authority);
    const response = await oauth.discoveryRequest(issuerUrl);
    this._issuer = await oauth.processDiscoveryResponse(issuerUrl, response);
    return this._issuer;
  }

  async getClaims(accessToken) {
    try {
      const issuer = await this._getIssuer();
      const client = {
        client_id: this.settings.client_id,
        token_endpoint_auth_method: 'none'
      };

      const userinfoResponse = await oauth.userInfoRequest(
        issuer,
        client,
        accessToken
      );

      const claims = await oauth.processUserInfoResponse(
        issuer,
        client,
        oauth.skipSubjectCheck,
        userinfoResponse
      );

      return claims;
    } catch (error) {
      console.error('Error getting user claims:', error);
      throw error;
    }
  }
}

export const configure = (config) => {
  if (config) {
    return new UserManager({
      authority: config.AUTHORITY,
      redirect_uri: config.REDIRECT_URL,
      post_logout_redirect_uri: config.POST_LOGOUT_REDIRECT_URL,
      client_id: config.CLIENT_ID,
      response_type: 'code',
      scope: 'openid role I4ProAuth.Security.Api I4ProPortal.Api IdentityServerApi',
      userStore: localStorage,
      clockTolerance: config.CLOCK_TOLERANCE ?? 300,
      enableDebug: config.ENABLE_DEBUG ?? false
    });
  }

  throw new Error("Obrigatório passar como parâmetro uma configuração!");
}

export const validateClientID = (config, urlKeyToClientID) => {
  if (!config.CLIENT_ID_EXTERNAL) return config;

  let clientIdExternal = config.CLIENT_ID_EXTERNAL;

  const newConfig = clientIdExternal[Object.keys(clientIdExternal).find(keyURL => keyURL.toLowerCase() === urlKeyToClientID.toLowerCase() )];

  return !newConfig ? config : newConfig;
}
